/**
 * /heat-stress-calculator — расчётное ядро без DOM.
 *
 * Что считает:
 *   • положение солнца (алгоритм NOAA по Миусу — высота и расстояние до Солнца);
 *   • приход солнечной радиации по облачности: ясное небо по Хаурвицу,
 *     облачность по Кастену–Чеплаку (у нас нет измеренной радиации — только
 *     прогноз облачности, поэтому это ОЦЕНКА, и тул так и говорит);
 *   • WBGT на улице по физической модели Liljegren et al. (2008): шар и
 *     естественный влажный термометр решаются из теплового баланса итерацией,
 *     ветер с 10 м пересчитывается на высоту прибора (2 м) по классу
 *     устойчивости — так, как это делает KNMI и реализация ECMWF thermofeel,
 *     с которой ядро сверено в tests/js/heat-stress-core.test.mjs;
 *   • индекс жары NWS (Ротфуз с поправками) — ДРУГОЕ число, для справки;
 *   • корейский «체감온도» (формула KMA с 02.06.2022, влажный термометр
 *     по Сталлу) — по нему в Корее включаются обязанности работодателя;
 *   • допустимый режим работа/отдых по формулам NIOSH 2016 (REL — для
 *     акклиматизированных, RAL — для остальных) на часовом взвешенном среднем,
 *     как требует сам NIOSH (раздел 8.1 и ссылка на ACGIH: «1-hour TWA»).
 *
 * Чего ядро НЕ делает и не утверждает: это не замер прибором WBGT. Ошибка
 * радиации по облачности — главная неопределённость; её размер измерен и
 * записан в docs/heat-stress-calculator.md.
 */
(function (root) {
'use strict';

var DEG = Math.PI / 180;

/* Константы модели Liljegren (2008) — те же, что в эталонном коде на C и в
   thermofeel. Менять их по одной нельзя: это одна откалиброванная модель. */
var STEFANB = 5.6696e-8, CP = 1003.5, M_AIR = 28.97, M_H2O = 18.015;
var R_GAS = 8314.34, R_AIR = R_GAS / M_AIR;
var PR = CP / (CP + 1.25 * R_AIR);
var RATIO = CP * M_AIR / M_H2O;
var EMIS_WICK = 0.95, ALB_WICK = 0.4, D_WICK = 0.007, L_WICK = 0.0254;
var EMIS_GLOBE = 0.95, ALB_GLOBE = 0.05, D_GLOBE = 0.0508;
var EMIS_SFC = 0.999, ALB_SFC = 0.45;
var CZA_MIN = 0.00873;          // cos(89.5°): ниже солнце считается севшим
var NORMSOLAR_MAX = 0.85;       // доля верхней границы атмосферы, выше не бывает
var SOLAR_CONST = 1367;
var MIN_SPEED = 0.13;           // пол скорости в числе Рейнольдса, м/с
var MIN_WIND_10M = 0.62;        // пол ветра на 10 м (KNMI), м/с
var CONVERGENCE = 0.02, MAX_ITER = 500;
var LSRDT = [
    [1, 1, 2, 4, 0, 5, 6, 0],
    [1, 2, 3, 4, 0, 5, 6, 0],
    [2, 2, 3, 4, 0, 4, 4, 0],
    [3, 3, 4, 4, 0, 0, 0, 0],
    [3, 4, 4, 4, 0, 0, 0, 0],
];
var URBAN_EXP = [0.15, 0.15, 0.20, 0.25, 0.30, 0.30];

function esat(tk) { var y = (tk - 273.15) / (tk - 32.18); return 1.004 * 6.1121 * Math.exp(17.502 * y); }
function dewPointK(e) { var z = Math.log(e / (6.1121 * 1.004)); return 273.15 + 240.97 * z / (17.502 - z); }
function viscosity(tk) {
    var omega = (tk / 97.0 - 2.9) / 0.4 * (-0.034) + 1.048;
    return 2.6693e-6 * Math.sqrt(M_AIR * tk) / (3.617 * 3.617 * omega);
}
function thermalCond(tk) { return (CP + 1.25 * R_AIR) * viscosity(tk); }
function diffusivity(tk, p) {
    var pc13 = Math.pow(36.4 * 218.0, 1 / 3), tc512 = Math.pow(132.0 * 647.3, 5 / 12);
    var tc12 = Math.sqrt(132.0 * 647.3), mmix = Math.sqrt(1 / M_AIR + 1 / M_H2O);
    return 3.640e-4 * Math.pow(tk / tc12, 2.334) * pc13 * tc512 * mmix / (p / 1013.25) * 1e-4;
}
function evap(tk) { return (313.15 - tk) / 30.0 * (-71100.0) + 2.4073e6; }
function emisAtm(tk, rh) { return 0.575 * Math.pow(rh * esat(tk), 0.143); }
function hSphere(tk, p, speed) {
    var dens = p * 100 / (R_AIR * tk);
    var re = Math.max(speed, MIN_SPEED) * dens * D_GLOBE / viscosity(tk);
    return (2.0 + 0.6 * Math.sqrt(re) * Math.pow(PR, 0.3333)) * thermalCond(tk) / D_GLOBE;
}
function hCylinder(tk, p, speed) {
    var dens = p * 100 / (R_AIR * tk);
    var re = Math.max(speed, MIN_SPEED) * dens * D_WICK / viscosity(tk);
    return 0.281 * Math.pow(re, 0.6) * Math.pow(PR, 0.44) * thermalCond(tk) / D_WICK;
}

/** Температура шара, °C. rh — доля. */
function globeC(tk, rh, p, speed, solar, fdir, cza) {
    var emis = emisAtm(tk, rh), czaS = cza > CZA_MIN ? cza : 1;
    var beam = fdir > 0 ? fdir * (1 / (2 * czaS) - 1) : 0;
    var prev = tk, next = tk;
    for (var i = 0; i < MAX_ITER; i++) {
        var h = hSphere(0.5 * (prev + tk), p, speed);
        next = Math.pow(0.5 * (emis * Math.pow(tk, 4) + EMIS_SFC * Math.pow(tk, 4))
            - h / (STEFANB * EMIS_GLOBE) * (prev - tk)
            + solar / (2 * STEFANB * EMIS_GLOBE) * (1 - ALB_GLOBE) * (beam + 1 + ALB_SFC), 0.25);
        if (Math.abs(next - prev) < CONVERGENCE) return next - 273.15;
        prev = 0.9 * prev + 0.1 * next;
    }
    return NaN;
}

/** Влажный термометр, °C: rad=1 — естественный (в WBGT), 0 — психрометрический. */
function wetBulbC(tk, rh, p, speed, solar, fdir, cza, rad) {
    var czaS = cza > CZA_MIN ? cza : 1;
    var sza = Math.acos(Math.max(-1, Math.min(1, czaS)));
    var emis = emisAtm(tk, rh), eair = rh * esat(tk);
    var prev = dewPointK(eair), next = prev;
    for (var i = 0; i < MAX_ITER; i++) {
        var tref = 0.5 * (prev + tk);
        var h = hCylinder(tref, p, speed);
        var fatm = STEFANB * EMIS_WICK * (0.5 * (emis * Math.pow(tk, 4) + EMIS_SFC * Math.pow(tk, 4)) - Math.pow(prev, 4))
            + (1 - ALB_WICK) * solar * ((1 - fdir) * (1 + 0.25 * D_WICK / L_WICK)
            + fdir * (Math.tan(sza) / Math.PI + 0.25 * D_WICK / L_WICK) + ALB_SFC);
        var ewick = esat(prev);
        var dens = p * 100 / (R_AIR * tref);
        var sc = viscosity(tref) / (dens * diffusivity(tref, p));
        next = tk - evap(tref) / RATIO * (ewick - eair) / (p - ewick) * Math.pow(PR / sc, 0.56) + (fatm / h) * rad;
        if (Math.abs(next - prev) < CONVERGENCE) return next - 273.15;
        prev = 0.9 * prev + 0.1 * next;
    }
    return NaN;
}

/** Ветер на 2 м из ветра на 10 м — профиль по классу устойчивости (городской). */
function wind2m(va10, cza, solar) {
    va10 = Math.max(va10, MIN_WIND_10M);
    var day = cza > 0;
    var col = day ? (solar >= 925 ? 0 : solar >= 675 ? 1 : solar >= 175 ? 2 : 3) : 5;
    var row = day
        ? (va10 >= 6 ? 4 : va10 >= 5 ? 3 : va10 >= 3 ? 2 : va10 >= 2 ? 1 : 0)
        : (va10 >= 2.5 ? 2 : va10 >= 2 ? 1 : 0);
    var cls = LSRDT[row][col];
    return Math.max(va10 * Math.pow(2 / 10, URBAN_EXP[cls - 1]), MIN_SPEED);
}

/**
 * WBGT на улице. inp: { ta °C, rh %, p гПа, va10 м/с, solar Вт/м², fdir 0..1, cza }.
 * solar/fdir/cza — уже согласованные (solarParams). Возвращает °C и составляющие.
 */
function wbgtLiljegren(inp) {
    var tk = inp.ta + 273.15, rh = Math.max(0.01, Math.min(1, inp.rh / 100));
    var p = inp.p || 1013.25, cza = inp.cza, solar = Math.max(0, inp.solar || 0);
    var fdir = Math.max(0, Math.min(0.9, inp.fdir || 0));
    if (cza < CZA_MIN) fdir = 0;
    /* va2 — ветер, измеренный на месте работы (ручной ввод): профиль 10 → 2 м
       к нему не применяется. */
    var speed = inp.va2 != null ? Math.max(inp.va2, MIN_SPEED)
        : wind2m(inp.va10 == null ? 2 : inp.va10, cza, solar);
    var tg = globeC(tk, rh, p, speed, solar, fdir, cza);
    var tnw = wetBulbC(tk, rh, p, speed, solar, fdir, cza, 1);
    return { wbgt: 0.1 * inp.ta + 0.2 * tg + 0.7 * tnw, tg: tg, tnw: tnw, speed2m: speed };
}

/* ── Солнце ─────────────────────────────────────────────────────────────── */

/** Высота Солнца (°), cos зенита и расстояние (а.е.) на момент ms (UTC). */
function sunPosition(ms, lat, lon) {
    var jd = ms / 86400000 + 2440587.5, jc = (jd - 2451545) / 36525;
    var L0 = (280.46646 + jc * (36000.76983 + jc * 0.0003032)) % 360;
    var M = 357.52911 + jc * (35999.05029 - 0.0001537 * jc);
    var ecc = 0.016708634 - jc * (0.000042037 + 0.0000001267 * jc);
    var Mr = M * DEG;
    var ctr = Math.sin(Mr) * (1.914602 - jc * (0.004817 + 0.000014 * jc))
        + Math.sin(2 * Mr) * (0.019993 - 0.000101 * jc) + Math.sin(3 * Mr) * 0.000289;
    var trueLong = L0 + ctr, trueAnom = M + ctr;
    var dist = (1.000001018 * (1 - ecc * ecc)) / (1 + ecc * Math.cos(trueAnom * DEG));
    var omega = 125.04 - 1934.136 * jc;
    var appLong = trueLong - 0.00569 - 0.00478 * Math.sin(omega * DEG);
    var obliq0 = 23 + (26 + ((21.448 - jc * (46.815 + jc * (0.00059 - jc * 0.001813)))) / 60) / 60;
    var obliq = obliq0 + 0.00256 * Math.cos(omega * DEG);
    var decl = Math.asin(Math.sin(obliq * DEG) * Math.sin(appLong * DEG));
    var y = Math.pow(Math.tan(obliq * DEG / 2), 2), l0 = L0 * DEG;
    var eqTime = 4 / DEG * (y * Math.sin(2 * l0) - 2 * ecc * Math.sin(Mr)
        + 4 * ecc * y * Math.sin(Mr) * Math.cos(2 * l0) - 0.5 * y * y * Math.sin(4 * l0)
        - 1.25 * ecc * ecc * Math.sin(2 * Mr));
    var d = new Date(ms);
    var minutes = d.getUTCHours() * 60 + d.getUTCMinutes() + d.getUTCSeconds() / 60;
    var tst = ((minutes + eqTime + 4 * lon) % 1440 + 1440) % 1440;
    var ha = tst / 4 < 0 ? tst / 4 + 180 : tst / 4 - 180;
    var cz = Math.sin(lat * DEG) * Math.sin(decl) + Math.cos(lat * DEG) * Math.cos(decl) * Math.cos(ha * DEG);
    cz = Math.max(-1, Math.min(1, cz));
    return { elev: 90 - Math.acos(cz) / DEG, cza: cz, dist: dist };
}

/** Ясное небо, глобальная на горизонтальную (Хаурвиц, 1945), Вт/м². */
function clearSkyGhi(cza) { return cza > 0 ? 1098 * cza * Math.exp(-0.059 / cza) : 0; }

/**
 * Облачность (0..100 %) → доля ясного неба. Форма Кастена–Чеплака (1980),
 * коэффициенты подобраны по ИЗМЕРЕННОЙ радиации DWD (Потсдам и Линденберг,
 * июнь–август 2026, 2363 часа с солнцем выше 5°) с перекрёстной проверкой:
 * подбор на одной станции, проверка на другой. Классические 0,75 / 3,4
 * занижали WBGT на солнце в среднем на 0,3 °C (средняя ошибка 0,65 °C) — то
 * есть ошибались в опасную сторону; 0,5 / 2,25 дают 0,51 °C на чужой станции
 * и сдвиг +0,14 °C — в сторону осторожности. Замер — docs/heat-stress-calculator.md.
 */
function cloudFactor(cloudPct) {
    var n = Math.max(0, Math.min(1, (cloudPct || 0) / 100));
    return 1 - 0.5 * Math.pow(n, 2.25);
}

/** Как в коде Liljegren: потолок по верхней границе и доля прямого луча. */
function solarParams(ghi, cza, dist) {
    var toa = SOLAR_CONST * Math.max(0, cza) / (dist * dist);
    if (cza < CZA_MIN) toa = 0;
    if (toa <= 0) return { solar: 0, fdir: 0 };
    var norm = Math.min(ghi / toa, NORMSOLAR_MAX);
    var fdir = norm > 0 ? Math.max(0, Math.min(0.9, Math.exp(3 - 1.34 * norm - 1.65 / norm))) : 0;
    return { solar: norm * toa, fdir: fdir };
}

/**
 * Один час прогноза → WBGT на солнце и в тени.
 * h: { t ISO, temp °C, hum %, wind м/с (10 м), cloud %, press гПа, dew °C }.
 * В тени убирается прямой луч, рассеянный свет остаётся — это навес или
 * тень здания, а не помещение.
 */
function hourWbgt(h, lat, lon) {
    var ms = Date.parse(h.t);
    var sun = sunPosition(ms, lat, lon);
    var rh = h.hum != null ? h.hum : (h.dew != null ? rhFromDew(h.temp, h.dew) : 50);
    var ghi = clearSkyGhi(sun.cza) * cloudFactor(h.cloud);
    var sp = solarParams(ghi, sun.cza, sun.dist);
    var base = { ta: h.temp, rh: rh, p: h.press || 1013.25, va10: h.wind == null ? 2 : h.wind, cza: sun.cza };
    var inSun = wbgtLiljegren(Object.assign({}, base, { solar: sp.solar, fdir: sp.fdir }));
    var inShade = wbgtLiljegren(Object.assign({}, base, { solar: sp.solar * (1 - sp.fdir), fdir: 0 }));
    return { sun: inSun.wbgt, shade: inShade.wbgt, ghi: sp.solar, elev: sun.elev, rh: rh, tg: inSun.tg, tnw: inSun.tnw };
}

/** Относительная влажность по точке росы (Магнус, Алдухов–Эскридж). */
function rhFromDew(t, td) {
    var a = 17.625, b = 243.04;
    return Math.max(1, Math.min(100, 100 * Math.exp(a * td / (b + td)) / Math.exp(a * t / (b + t))));
}

/* ── Справочные индексы ─────────────────────────────────────────────────── */

/** Индекс жары NWS, °C (вход °C и %). Алгоритм WPC: простая формула, регрессия Ротфуза, поправки. */
function heatIndexC(tc, rh) {
    var t = tc * 9 / 5 + 32;
    if (t <= 40) return tc;
    var hi = 0.5 * (t + 61 + (t - 68) * 1.2 + rh * 0.094);
    if ((hi + t) / 2 >= 80) {
        hi = -42.379 + 2.04901523 * t + 10.14333127 * rh - 0.22475541 * t * rh
            - 6.83783e-3 * t * t - 5.481717e-2 * rh * rh + 1.22874e-3 * t * t * rh
            + 8.5282e-4 * t * rh * rh - 1.99e-6 * t * t * rh * rh;
        if (rh < 13 && t >= 80 && t <= 112) hi -= ((13 - rh) / 4) * Math.sqrt(Math.max(0, 17 - Math.abs(t - 95)) / 17);
        else if (rh > 85 && t >= 80 && t <= 87) hi += ((rh - 85) / 10) * ((87 - t) / 5);
    }
    return (hi - 32) * 5 / 9;
}

/** Влажный термометр по Сталлу (2011), °C. */
function stullWetBulb(t, rh) {
    return t * Math.atan(0.151977 * Math.sqrt(rh + 8.313659)) + Math.atan(t + rh) - Math.atan(rh - 1.67633)
        + 0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 4.686035;
}

/** Летний 체감온도 KMA (с 02.06.2022), °C. */
function kmaFeels(t, rh) {
    var tw = stullWetBulb(t, rh);
    return -0.2442 + 0.55399 * tw + 0.45535 * t - 0.0022 * tw * tw + 0.00278 * tw * t + 3.0;
}

/* ── Нормы ──────────────────────────────────────────────────────────────── */

/* Метаболизм по классам (Вт, «стандартный человек» 1,8 м²): представительные
   значения ISO 8996, те же, что NIOSH 2016 называет границами тяжести работ;
   отдых — 117 Вт (NIOSH, табл. 5-1). */
var MET = { rest: 117, light: 180, moderate: 300, heavy: 415, vheavy: 520 };

/* Поправка на одежду, °C-WBGT — NIOSH 2016, табл. 3-2 (столбец 2006). */
var CLOTHING = { work: 0, coveralls: 0, double: 3, sms: 0.5, polyolefin: 1, barrier: 11 };

/** Предел WBGT, °C, для среднечасового метаболизма M (Вт). REL — акклиматизированным, RAL — нет. */
function limitWbgt(m, acclimatized) {
    return acclimatized ? 56.7 - 11.5 * Math.log10(m) : 59.9 - 14.1 * Math.log10(m);
}

/**
 * Режим на час: какую долю часа можно работать (1, 0.75, 0.5, 0.25 или 0).
 * work — WBGT на месте работы, rest — WBGT на месте отдыха (°C), оба без поправки
 * на одежду (её добавляем здесь), level — ключ MET.
 * Взвешенное среднее и по WBGT, и по метаболизму — как требует NIOSH.
 */
function workRest(work, rest, level, acclimatized, clothing) {
    var cav = CLOTHING[clothing] || 0, mw = MET[level] || MET.moderate, mr = MET.rest;
    var steps = [1, 0.75, 0.5, 0.25];
    for (var i = 0; i < steps.length; i++) {
        var f = steps[i];
        var w = f * (work + cav) + (1 - f) * (rest + cav);
        var lim = limitWbgt(f * mw + (1 - f) * mr, acclimatized);
        if (w <= lim) return { f: f, margin: lim - w, limit: lim, eff: w };
    }
    var restOnly = rest + cav, restLim = limitWbgt(mr, acclimatized);
    return { f: 0, margin: restLim - restOnly, limit: restLim, eff: restOnly, restUnsafe: restOnly > restLim };
}

/* Россия: классы условий труда по ТНС-индексу для ПРОИЗВОДСТВЕННЫХ ПОМЕЩЕНИЙ с
   нагревающим микроклиматом — методика СОУТ, приказ Минтруда № 817н
   (в силе 01.09.2024–01.09.2030), прил. 6; лёгкая летняя одежда 0,5–0,8 кло.
   Границы — верхние значения класса; категория — по энергозатратам (Вт). */
var SOUT = {
    Ia:  [26.4, 26.6, 27.4, 28.6, 31.0],
    Ib:  [25.8, 26.1, 26.9, 27.9, 30.3],
    IIa: [25.1, 25.5, 26.2, 27.3, 29.9],
    IIb: [23.9, 24.2, 25.0, 26.4, 29.1],
    III: [21.8, 22.0, 23.4, 25.7, 27.9],
};
var SOUT_CLASSES = ['2', '3.1', '3.2', '3.3', '3.4', '4'];
function soutCategory(m) { return m <= 139 ? 'Ia' : m <= 174 ? 'Ib' : m <= 232 ? 'IIa' : m <= 290 ? 'IIb' : 'III'; }
function soutClass(tns, level) {
    var cat = soutCategory(MET[level] || MET.moderate), b = SOUT[cat];
    var v = Math.round(tns * 10) / 10;
    for (var i = 0; i < b.length; i++) if (v <= b[i]) return { cat: cat, cls: SOUT_CLASSES[i] };
    return { cat: cat, cls: '4' };
}

/* Залив: летний запрет работ на открытом солнце в полдень (часы местные).
   from/to — [месяц, день] включительно, h — [начало, конец) в часах.
   Сверено 22.10.2026 с официальными источниками (до этого — с обзорами юристов;
   числа совпали все):
     AE — постановление МОHRE № 44/2022: «12.30 pm – 3 pm, 15 June – 15 September
          every year» (u.ae, раздел Working hours);
     SA — HRSD, новость 23.06.2026: 15.06–15.09.2026, 12:00–15:00;
     QA — решение Минтруда № 17/2021 (МОТ, «New legislation in Qatar…»):
          10:00–15:30, 1.06–15.09; и отдельно WBGT > 32,1 — см. QA_WBGT_STOP;
     KW — решение PAM № 3/2026 (Al Jarida 20.05.2026 со слов гендиректора PAM):
          11:00–16:00, 1.06–31.08;
     OM — заявление Минтруда 04.05.2026 (Times of Oman): 12:30–15:30, июнь–август;
     BH — решение Минтруда № 5/2026 от 02.04.2026 (lloc.gov.bh, RLAB0526, ст. 1):
          12:00–16:00, 15.06–31.08 «من كل عام». */
var MIDDAY_BAN = {
    AE: { from: [6, 15], to: [9, 15], h: [12.5, 15] },
    SA: { from: [6, 15], to: [9, 15], h: [12, 15] },
    QA: { from: [6, 1],  to: [9, 15], h: [10, 15.5] },
    KW: { from: [6, 1],  to: [8, 31], h: [11, 16] },
    OM: { from: [6, 1],  to: [8, 31], h: [12.5, 15.5] },
    BH: { from: [6, 15], to: [8, 31], h: [12, 16] },
};
/** Попадает ли местное время (месяц, день, час с дробью) в запрет страны cc. */
function middayBan(cc, month, day, hour) {
    var r = MIDDAY_BAN[cc];
    if (!r) return false;
    var md = month * 100 + day;
    if (md < r.from[0] * 100 + r.from[1] || md > r.to[0] * 100 + r.to[1]) return false;
    return hour >= r.h[0] && hour < r.h[1];
}

/* Катар, решение Минтруда № 17/2021: «all work must stop if the WBGT rises
   beyond 32.1° in a particular workplace» — в любой час и в любой месяц
   (МОТ, «New legislation in Qatar provides greater protection to workers from
   heat stress»). Решает замер на месте; по прогнозу — предупреждение. */
var QA_WBGT_STOP = 32.1;
function wbgtStop(cc, wbgt) { return cc === 'QA' && wbgt > QA_WBGT_STOP; }

var api = {
    sunPosition: sunPosition, clearSkyGhi: clearSkyGhi, cloudFactor: cloudFactor,
    solarParams: solarParams, wind2m: wind2m, wbgtLiljegren: wbgtLiljegren, hourWbgt: hourWbgt,
    rhFromDew: rhFromDew, heatIndexC: heatIndexC, stullWetBulb: stullWetBulb, kmaFeels: kmaFeels,
    limitWbgt: limitWbgt, workRest: workRest, soutClass: soutClass, soutCategory: soutCategory,
    middayBan: middayBan, MIDDAY_BAN: MIDDAY_BAN, MET: MET, CLOTHING: CLOTHING,
    wbgtStop: wbgtStop, QA_WBGT_STOP: QA_WBGT_STOP,
};
root.HeatStress = api;
if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
